import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Check, Download } from "lucide-react";
import SEO from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { PageHero, PrimaryLink } from "@/components/site";
import { api } from "@/lib/api";
import { CONTACT, COMPANY } from "@/data/content";

const PILLAR_ORDER = ["Technology Advisory", "Cyber Security Advisory", "Governance & Resilience", "Transformation & Enablement"];

export default function Brochure() {
  const [packages, setPackages] = useState([]);
  const [services, setServices] = useState([]);
  useEffect(() => {
    api.get("/packages").then(({ data }) => setPackages(data)).catch(() => {});
    api.get("/services").then(({ data }) => setServices(data)).catch(() => {});
  }, []);
  const grouped = PILLAR_ORDER.map((pillar) => ({ pillar, items: services.filter((s) => s.pillar === pillar) }));

  return (
    <div data-testid="page-brochure">
      <SEO title="Full Pricing Brochure" description="TWORAY Ltd full pricing brochure — packages and individual service pricing." />
      <PageHero overline="Full Pricing Brochure" title="Everything, with the numbers."
        subtitle="Your complete guide to our packages and individual service pricing. All prices exclude VAT and are indicative, subject to scope." />

      <section className="py-16 sm:py-20">
        <div className="mx-auto max-w-5xl px-5 sm:px-8">
          <div className="flex items-center justify-between mb-10">
            <p className="text-sm text-slate">Prepared for prospective clients · v1</p>
            <button onClick={() => window.print()} data-testid="brochure-print-btn" className="inline-flex items-center gap-2 bg-navy text-white font-semibold text-sm px-4 py-2.5 rounded-sm hover:bg-brandblue transition-colors"><Download size={16} /> Save as PDF</button>
          </div>

          <h2 className="font-heading font-bold text-2xl text-navy mb-6">Advisory packages</h2>
          <div className="grid gap-5 lg:grid-cols-3">
            {packages.map((p) => (
              <Reveal key={p.slug}>
                <div className={`rounded-md border p-6 h-full ${p.recommended ? "border-brandblue bg-lightbg" : "border-brandborder bg-white"}`} data-testid={`brochure-package-${p.slug}`}>
                  <h3 className="font-heading font-bold text-xl text-navy">{p.name}</h3>
                  <div className="mt-2 font-heading font-extrabold text-3xl text-brandblue">{p.price}</div>
                  <p className="text-xs text-slate mt-1">{p.price_note}{p.value_note ? ` · ${p.value_note}` : ""}</p>
                  <ul className="mt-5 space-y-2">
                    {p.features.map((f) => f.toLowerCase().startsWith("everything in")
                      ? <li key={f} className="text-sm font-semibold text-navy">{f}</li>
                      : <li key={f} className="flex items-start gap-2 text-sm text-navy"><Check size={16} className="mt-0.5 text-brandblue shrink-0" />{f}</li>)}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <h2 className="font-heading font-bold text-2xl text-navy mt-16 mb-6">Individual services</h2>
          <div className="space-y-10">
            {grouped.map((g) => (
              <div key={g.pillar}>
                <h3 className="font-heading font-semibold text-lg text-brandblue border-b border-brandborder pb-3">{g.pillar}</h3>
                <div className="mt-4 divide-y divide-brandborder">
                  {g.items.map((s) => (
                    <div key={s.slug} className="flex items-start justify-between gap-4 py-3" data-testid={`brochure-svc-${s.slug}`}>
                      <div><div className="font-semibold text-navy">{s.name}</div><div className="text-sm text-slate">{s.summary}</div></div>
                      <div className="font-heading font-bold text-navy whitespace-nowrap">{s.price}</div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>

          <p className="mt-8 text-xs text-slate italic">All prices exclude VAT and are indicative, confirmed against scope in a written proposal. {COMPANY.name}.</p>

          <Reveal>
            <div className="mt-14 bg-navy rounded-md p-10 text-white text-center grain-overlay relative overflow-hidden">
              <div className="relative z-10">
                <h3 className="font-heading font-bold text-2xl sm:text-3xl">Ready to scope your engagement?</h3>
                <p className="mt-3 text-white/70">Book a discovery call or email <a href={`mailto:${CONTACT.email}`} className="text-cyan font-semibold">{CONTACT.email}</a>.</p>
                <PrimaryLink to="/book" dark className="mt-6" data-testid="brochure-book-cta">Book a Discovery Call</PrimaryLink>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
